/**
 * Utilities for inserting Markdown syntax into a textarea (e.g. card description editor).
 * Used with a controlled textarea: returns the new value; caller updates state and restores selection.
 */

/**
 * Inserts Markdown around the current selection in a textarea.
 * If there is a selection, wraps it with `before` and `after`.
 * If the selection is collapsed, inserts `before` and `after` at cursor and places cursor between them.
 * Returns the new text value and the new cursor position (start, end) for the "content" between the delimiters.
 */
export function insertMarkdownAroundSelection(
  textarea: HTMLTextAreaElement,
  before: string,
  after: string,
): { newValue: string; selectionStart: number; selectionEnd: number } {
  const start = textarea.selectionStart;
  const end = textarea.selectionEnd;
  const text = textarea.value;
  const selected = text.slice(start, end);

  const beforeText = text.slice(0, start);
  const afterText = text.slice(end);

  const newValue = beforeText + before + selected + after + afterText;
  const newStart = start + before.length;
  const newEnd = newStart + selected.length;

  return { newValue, selectionStart: newStart, selectionEnd: newEnd };
}

/**
 * Inserts a bullet list marker at the start of the current line (or each selected line).
 * Returns the new value and selection range.
 */
export function insertBulletList(textarea: HTMLTextAreaElement): {
  newValue: string;
  selectionStart: number;
  selectionEnd: number;
} {
  return insertLinePrefix(textarea, '- ');
}

/**
 * Inserts a numbered list marker at the start of the current line (or first selected line).
 * Returns the new value and selection range.
 */
export function insertNumberedList(textarea: HTMLTextAreaElement): {
  newValue: string;
  selectionStart: number;
  selectionEnd: number;
} {
  return insertLinePrefix(textarea, '1. ');
}

function insertLinePrefix(
  textarea: HTMLTextAreaElement,
  prefix: string,
): { newValue: string; selectionStart: number; selectionEnd: number } {
  const text = textarea.value;
  const start = textarea.selectionStart;
  const end = textarea.selectionEnd;

  const lineStart = findLineStart(text, start);
  const before = text.slice(0, lineStart);
  const rest = text.slice(lineStart);
  const newValue = before + prefix + rest;
  const offset = prefix.length;
  const newStart = start + offset;
  const newEnd = end + offset;

  return { newValue, selectionStart: newStart, selectionEnd: newEnd };
}

function findLineStart(text: string, pos: number): number {
  let i = pos - 1;
  while (i >= 0 && text[i] !== '\n') i--;
  return i + 1;
}
